import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { useZorni } from "../../../../app/hooks/useZorni";
import { usePerson } from "../../../../app/hooks/user/usePerson";
import { useConversations } from "../../../../app/components/event/messenger/hooks/useConversations";
import { useAdmin } from "../../../hooks/useAdmin";
import { useOperation } from "../../../hooks/useOperation";
import { useListPersons } from "../../manageUsers/ManageUsers/hooks/useListPersons";
import {
  MT_ASSIGN_CONVERSATION,
  MT_PATCH_CONVERSATION_STATUS,
  QR_LIST_CONVERSATION,
} from "../graphQl/InboxAPI";
import { canArchiveConversation, canCloseConversation } from "../utils/conversationActions";

const PAGE_SIZE = 10;

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "");

const includesSearch = (haystack, needle) =>
  normalize(haystack).includes(normalize(needle));

const normalizePage = (page) => Math.max(1, Number(page) || 1);

export const useInbox = () => {
  const { userId } = useZorni();
  const { personInfo } = usePerson();
  const { organization, establishmentsPermission, accessToken, hasPaRole, manageableEstablishments } =
    useAdmin();
  const { runOperation, isLoading: isActionPending } = useOperation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPageState] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | OPEN | CLOSED | ARCHIVED
  const [assignmentFilter, setAssignmentFilter] = useState("all"); // all | assigned | unassigned
  const [selectedEstablishment, setSelectedEstablishment] = useState("all");

  const setCurrentPage = useCallback((page) => {
    setCurrentPageState(normalizePage(page));
  }, []);

  const canQuery = Boolean(organization && (establishmentsPermission || []).length > 0);

  // ── Filtre établissements : même source que /admin/circuits (liste centrale de useAdmin) ──

  const establishmentFilterOptions = useMemo(
    () => [
      { index: 0, value: "all", text: "Tous les établissements" },
      ...manageableEstablishments.map((item, index) => ({
        index: index + 1,
        value: item.id,
        text: item.company?.legalName,
      })),
    ],
    [manageableEstablishments]
  );

  useEffect(() => {
    setCurrentPageState(1);
  }, [selectedEstablishment, statusFilter, assignmentFilter, search]);

  // QR_LIST_CONVERSATION ne prend qu'un seul establishmentId (contrairement à
  // QR_LIST_TRIP_PUBLICATION) : "Tous les établissements" ne peut donc pas être scopé aux seuls
  // établissements gérables en un seul appel — voir note dans le rapport de livraison.
  const conversationEstablishmentId =
    selectedEstablishment !== "all" ? selectedEstablishment : undefined;

  // "Assignées à moi" : filtre server-side (renseigne assignedAgentId dans la requête de liste),
  // pas juste un filtre client — le backend n'autorise de toute façon assignedAgentId que s'il
  // correspond à l'utilisateur connecté (voir ConversationResolver.listConversation).
  const conversationAssignedAgentId =
    assignmentFilter === "mine" ? userId : undefined;

  // Statut : filtre server-side lui aussi (listConversation accepte `status` en paramètre).
  const conversationStatus = statusFilter !== "all" ? statusFilter : undefined;

  // Pagination serveur réelle (pages de 10) : useConversations n'expose pas de "goToPage", mais
  // son état interne de pagination reste à 0 tant que loadMoreConversations n'est jamais appelé
  // (ce que l'Inbox ne fait pas) — chaque changement de `initialPaginationParams` déclenche donc
  // un refetch Apollo (variables changées) et REMPLACE `conversations` plutôt que d'accumuler.
  const conversationsPaginationParams = useMemo(
    () => ({ currentPage: currentPage - 1, sizePage: PAGE_SIZE }),
    [currentPage]
  );

  // Réutilisation du hook existant (Messenger) plutôt qu'un hook dédié — voir options `status`
  // et `skip` additives ajoutées à useConversations pour l'usage admin, sans impact sur Messenger.
  // Pas besoin de passer explicitement le token : apolloClient.js l'injecte déjà par défaut.
  const {
    conversations,
    loading: conversationsLoading,
    error,
    conversationsTotalPages,
  } = useConversations(
    conversationsPaginationParams,
    conversationEstablishmentId,
    undefined,
    conversationAssignedAgentId,
    conversationStatus,
    { skip: !canQuery }
  );

  // ── Propriétaire : résolution assignedAgentId -> personne ──
  // - hasPaRole (PA/PSA) : plusieurs agents possibles -> QR_LIST_PERSON (attachementIds).
  // - sinon (PU) : l'utilisateur ne voit que ses propres conversations, assignedAgentId ne peut
  //   être que le sien -> on utilise directement personInfo (usePerson), pas d'appel réseau.
  const attachementIds = useMemo(() => {
    if (selectedEstablishment !== "all") return [selectedEstablishment];
    return manageableEstablishments.map((item) => item.id);
  }, [selectedEstablishment, manageableEstablishments]);

  const agentsQuery = useListPersons(
    {
      paginationParams: { currentPage: 0, sizePage: 1000 },
      attachementIds: attachementIds.length ? attachementIds : undefined,
    },
    {
      skip: !canQuery || !hasPaRole,
      fetchPolicy: "cache-first",
    }
  );

  const agentsById = useMemo(() => {
    const map = new Map();

    if (!hasPaRole) {
      if (userId && personInfo) map.set(userId, personInfo);
      return map;
    }

    const content = agentsQuery.data?.QR_LIST_PERSON?.data?.content || [];
    content.forEach((person) => map.set(person.id, person));
    return map;
  }, [hasPaRole, userId, personInfo, agentsQuery.data]);

  // ── Filtrage/tri client — s'applique à la page courante (10 éléments côté serveur) ──
  const pageItems = useMemo(() => {
    const items = [...conversations];

    items.sort((a, b) => {
      const ad = new Date(a?.lastMessage?.sendingDate || 0).getTime();
      const bd = new Date(b?.lastMessage?.sendingDate || 0).getTime();
      return bd - ad;
    });

    return items.filter((item) => {
      // statut et "assignées à moi" sont désormais filtrés côté serveur (voir
      // conversationStatus / conversationAssignedAgentId) ; seul "unassigned" (pas de valeur
      // assignedAgentId à filtrer côté serveur) reste un filtre client.
      if (assignmentFilter === "unassigned" && item.assignedAgentId) return false;

      if (!search) return true;

      const agent = item.assignedAgentId ? agentsById.get(item.assignedAgentId) : null;
      const agentName = agent ? `${agent.firstName || ""} ${agent.lastName || ""}` : "";
      const participants = (item.participants || []).join(" ");

      return (
        includesSearch(item.tripReference, search) ||
        includesSearch(item.customerId, search) ||
        includesSearch(agentName, search) ||
        includesSearch(participants, search) ||
        includesSearch(item?.lastMessage?.content, search)
      );
    });
  }, [conversations, assignmentFilter, search, agentsById]);

  const totalPages = Math.max(1, conversationsTotalPages || 1);

  // ── Actions de gestion ──
  const [assignConversationMutation] = useMutation(MT_ASSIGN_CONVERSATION);
  const [patchStatusMutation] = useMutation(MT_PATCH_CONVERSATION_STATUS);

  const onAssign = useCallback(
    (conversationId) =>
      runOperation(
        async () => {
          const response = await assignConversationMutation({
            variables: { conversationId },
          });
          const result = response?.data?.MT_ASSIGN_CONVERSATION;
          if (!result || result.error) {
            throw new Error(result?.error?.message || "Impossible d'assigner cette conversation.");
          }
        },
        {
          successMsg: "Conversation assignée avec succès.",
          errorMsg: "Impossible d'assigner cette conversation.",
          refetchQueries: [QR_LIST_CONVERSATION],
        }
      ),
    [assignConversationMutation, runOperation]
  );

  const patchStatus = useCallback(
    (conversationId, status, { successMsg, errorMsg }) =>
      runOperation(
        async () => {
          const response = await patchStatusMutation({
            variables: { conversationId, status },
          });
          const result = response?.data?.MT_PATCH_CONVERSATION_STATUS;
          if (!result || result.error) {
            throw new Error(result?.error?.message || errorMsg);
          }
        },
        {
          successMsg,
          errorMsg,
          refetchQueries: [QR_LIST_CONVERSATION],
        }
      ),
    [patchStatusMutation, runOperation]
  );

  // Garde-fou : même si l'UI ne propose plus le bouton pour un statut donné
  // (cf. InboxTable / conversationActions.js), un appel direct à onArchive/onClose
  // ne doit rien exécuter si le statut courant ne l'autorise pas. Le statut est
  // relu depuis la liste chargée plutôt que fait confiance à l'appelant.
  const onArchive = useCallback(
    (conversationId) => {
      const conversation = conversations.find((item) => item.id === conversationId);
      if (!canArchiveConversation(conversation?.status)) return Promise.resolve();

      return patchStatus(conversationId, "ARCHIVED", {
        successMsg: "Conversation archivée avec succès.",
        errorMsg: "Impossible d'archiver cette conversation.",
      });
    },
    [patchStatus, conversations]
  );

  const onClose = useCallback(
    (conversationId) => {
      const conversation = conversations.find((item) => item.id === conversationId);
      if (!canCloseConversation(conversation?.status)) return Promise.resolve();

      return patchStatus(conversationId, "CLOSED", {
        successMsg: "Conversation clôturée avec succès.",
        errorMsg: "Impossible de clôturer cette conversation.",
      });
    },
    [patchStatus, conversations]
  );

  const onOpen = useCallback(
    (conversationId) => navigate(`/messenger/${conversationId}`),
    [navigate]
  );

  return {
    userId,

    conversations: pageItems,
    conversationsLoading,
    error,

    currentPage,
    setCurrentPage,
    totalPages,

    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    assignmentFilter,
    setAssignmentFilter,

    selectedEstablishment,
    setSelectedEstablishment,
    establishmentFilterOptions,

    agentsById,

    isActionPending,
    onAssign,
    onArchive,
    onClose,
    onOpen,
  };
};
